import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { PublicStore, PublicBooking, PublicSlot } from "@/api/types";
import { useBrand } from "@/brand/BrandProvider";
import { Badge, tx } from "@/components/ui";
import { closedOn } from "@/lib/opening";
import { PublicShell, reasonText } from "@/public/PublicShell";
import { BottomBar, DayStrip, InfoBox, Lbl, PBtn, PLink, SlotGroups, SummaryCard } from "@/public/pieces";

const fmtTime = (iso: string, tz: string, lang: string) => new Date(iso).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit", timeZone: tz });
const fmtDate = (iso: string, tz: string, lang: string) => new Date(iso).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: tz });
const fmtWhen = (d: Date, tz: string, lang: string) => d.toLocaleString(lang, { weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit", timeZone: tz });
const plusDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const money = (v: string | number) => `${Number(v).toLocaleString("el-GR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

/** /b/:token — the link from the confirmation e-mail: see, move or cancel one booking without an account. */
export function ManageBooking() {
  const { token = "" } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const brand = useBrand();
  const q = useQuery({ queryKey: ["pub-booking", token], queryFn: () => api<PublicBooking>(`/public/bookings/${token}`), retry: false });
  const [mode, setMode] = useState<"view" | "reschedule" | "cancel">("view");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState<PublicSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const b = q.data;
  const st = useQuery({ queryKey: ["pub-store", b?.store.slug], queryFn: () => api<PublicStore>(`/public/stores/${b!.store.slug}`), enabled: !!b });
  const avail = useQuery({
    queryKey: ["pub-avail-manage", token, date],
    queryFn: () => api<{ closed: boolean; slots: PublicSlot[] }>(`/public/availability?store=${b!.store.slug}&service_id=${b!.service.id}&date=${date}&party=1`),
    enabled: !!b && mode === "reschedule" && !!date,
  });
  const cancel = useMutation({
    mutationFn: () => api<{ penalty: boolean }>(`/public/bookings/${token}/cancel`, { method: "POST" }),
    onSuccess: () => { setMode("view"); setNotice(t("manage.cancelled")); q.refetch(); },
    onError: (e) => setError(e instanceof ApiError ? reasonText(t, e.detail) : t("app.error")),
  });
  const move = useMutation({
    mutationFn: () => api<PublicBooking>(`/public/bookings/${token}/reschedule`, { method: "POST", body: JSON.stringify({ starts_at: slot!.start }) }),
    onSuccess: () => { setMode("view"); setSlot(null); setNotice(t("manage.rescheduled")); q.refetch(); },
    onError: (e) => { setError(e instanceof ApiError ? reasonText(t, e.detail) : t("app.error")); avail.refetch(); },
  });
  if (q.isError) return <PublicShell><InfoBox tone="warn">{t("manage.notFound")}</InfoBox></PublicShell>;
  if (!b || !brand) return <PublicShell><p className="text-muted">{t("app.loading")}</p></PublicShell>;
  const tone = b.status === "scheduled" ? "accent" : b.status === "completed" ? "green" : b.status === "cancelled" ? "red" : "amber";
  const until = new Date(new Date(b.starts_at).getTime() - b.cutoff_hours * 3600000);
  const active = b.status === "scheduled";
  return (
    <PublicShell title={t("manage.title")}>
      {notice && <div className="mb-3"><InfoBox tone="ok">{notice}</InfoBox></div>}
      {error && <p className="mb-3 rounded-[10px] bg-danger-soft px-3 py-2.5 text-[13px] text-danger" role="alert">{error}</p>}
      <h2 className="mb-3 flex items-center gap-2 text-xl font-semibold">{t("manage.title")} <Badge tone={tone}>{t(`status.${b.status}`)}</Badge></h2>
      <SummaryCard rows={[
        { k: t("book.step.service"), v: `${tx(b.service.name, lang)}${b.is_duo ? ` · ${t("book.forTwo")}${b.partner_name ? ` · ${b.partner_name}` : ""}` : ""} · ${money(b.price)}` },
        { k: t("appt.when"), v: `${fmtDate(b.starts_at, b.tz, lang)} · ${fmtTime(b.starts_at, b.tz, lang)}–${fmtTime(b.ends_at, b.tz, lang)}` },
        ...(b.staff_name ? [{ k: t("appt.staff"), v: b.staff_name }] : []),
        ...(b.training_minutes ? [{ k: "", v: t("me.trainingLasts", { minutes: b.training_minutes }) }] : []),
        { k: t("nav.store"), v: `${tx(b.store.name, lang)}${b.store.address ? ` · ${b.store.address}` : ""}${b.store.phone ? ` · ${b.store.phone}` : ""}` },
      ]} />
      {active && <div className="mt-3"><InfoBox tone={b.can_change_free ? "info" : "warn"}>{b.can_change_free ? `${t("me.cutoffInfo", { hours: b.cutoff_hours })} (${t("manage.until", { until: fmtWhen(until, b.tz, lang) })})` : t("manage.insideCutoff", { hours: b.cutoff_hours })}</InfoBox></div>}

      {active && mode === "view" && (
        <>
          <div className="mt-4 grid gap-2">
            <PLink href={`/api/v1/public/bookings/${token}/ics`}>＋ {t("book.addToCalendar")}</PLink>
            {!b.is_duo && <PBtn type="button" variant="secondary" disabled={!b.can_change_free} title={!b.can_change_free ? t("manage.insideCutoff", { hours: b.cutoff_hours }) : undefined} onClick={() => { setMode("reschedule"); setDate(b.starts_at.slice(0, 10)); }}>{t("manage.change")}</PBtn>}
            {b.is_duo && <p className="text-xs text-muted">{t("manage.duoNote")}</p>}
          </div>
          <Lbl>{t("manage.cancelTitle")}</Lbl>
          <PBtn type="button" variant="danger" className="w-full" onClick={() => setMode("cancel")}>{t("appt.cancel")}</PBtn>
        </>
      )}
      {mode === "cancel" && (
        <div className="mt-4 space-y-3">
          <InfoBox tone="warn">{b.can_change_free ? t("me.cancelConfirm") : t("me.cancelPenalty")}</InfoBox>
          <div className="grid grid-cols-2 gap-2"><PBtn type="button" variant="secondary" onClick={() => setMode("view")}>{t("manage.keep")}</PBtn><PBtn type="button" variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>{t("appt.cancel")}</PBtn></div>
        </div>
      )}
      {mode === "reschedule" && (
        <section>
          <Lbl>{t("manage.change")}</Lbl>
          <DayStrip lang={lang} min={new Date().toISOString().slice(0, 10)} max={plusDays(brand.booking_horizon_days)} isDisabled={(iso) => (st.data ? closedOn(st.data, iso) : false)} value={date} onChange={(d) => { setDate(d); setSlot(null); }} />
          {avail.data?.closed && <div className="mt-3"><InfoBox tone="warn">{t("book.closedDay")}</InfoBox></div>}
          {avail.data && !avail.data.closed && avail.data.slots.length === 0 && <div className="mt-3"><InfoBox tone="warn">{t("book.fullDay")}</InfoBox></div>}
          {avail.data && avail.data.slots.length > 0 && <SlotGroups slots={avail.data.slots} value={slot} onChange={setSlot} tz={b.tz} lang={lang} />}
          <BottomBar>
            <PBtn type="button" variant="quiet" onClick={() => setMode("view")}>{t("manage.keep")}</PBtn>
            <PBtn type="button" className="flex-1" disabled={!slot || move.isPending} onClick={() => move.mutate()}>{slot ? t("manage.changeTo", { when: fmtWhen(new Date(slot.start), b.tz, lang) }) : t("manage.apply")}</PBtn>
          </BottomBar>
        </section>
      )}
      {!active && <div className="mt-4"><PLink href={b.book_url} className="w-full">{t("manage.bookAgain")}</PLink></div>}
    </PublicShell>
  );
}
